import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import { get } from 'lodash';

import InputLabelTooltip from '~/components/InputLabelTooltip';
import MultiPicklist from '~/components/MultiPicklist';
import NestedMultiselect from '~/components/NestedMultiselect';
import TextField from '~/components/RedesignedTextField';
import SlackLogo from '~/images/destinations/Slack.svg';
import { hasError } from '~/lib/graphql';
import { legacySegmentType, readableSingularSegmentType } from '~/lib/segments';
import DeliverTestButton from '~/pages/destinations/components/DeliverTestButton';
import Disconnected from '~/pages/destinations/components/Disconnected';
import SuppressionForm from '~/pages/destinations/components/SuppressionForm';
import TriggerType from '~/pages/destinations/components/TriggerType';
import defaultTriggerTypes from '~/pages/destinations/components/TriggerType/defaultValues';

import sharedStyles from '../styles';
import SlackConnectorStatus from './SlackConnectorStatus';
import styles from './styles';

export function combineUsersAndChannels(slackUsers, slackChannels) {
  return slackUsers
    .map((user) => ({
      id: user.id,
      realName: user.profile.realName,
      displayName: user.profile.displayName,
      isChannel: false,
    }))
    .concat(
      slackChannels
        .map(({ id, name }) => ({
          id,
          name,
          options: [],
          isChannel: true,
        }))
        .filter((channel) => !!channel.name),
    );
}

class Slack extends React.Component {
  state = {
    columns: [],
    duplicateSuppressionTimeframe: 0,
    triggerType: 'segment_enter',
    channels: [],
    webhookUrl: undefined,
  };

  UNSAFE_componentWillMount() {
    if (this.props.destination) {
      this.setState({
        ...this.props.destination,
        ...this.props.destination.config,
      });
    }
  }

  connectedSlack = () => {
    const { accountConnector } = this.props;

    return get(accountConnector, 'status') === 'active';
  };

  handleFieldChange = (name, value) => {
    this.setState({ [name]: value }, this.handleInputChange);
  };

  handleInputChange = () => {
    const { onInputChange } = this.props;

    const {
      columns,
      duplicateSuppressionTimeframe,
      triggerType,
      channels,
      webhookUrl,
      message,
    } = this.state;

    onInputChange({
      config: {
        columns,
        duplicateSuppressionTimeframe,
        webhookUrl: channels ? null : webhookUrl,
        channels,
        message,
      },
      triggerType,
    });
  };

  renderWebhookInput() {
    const { classes, errors } = this.props;
    const { webhookUrl } = this.state;

    return (
      <TextField
        className={classes.field}
        error={hasError(errors, 'webhookUrl')}
        fullWidth
        value={webhookUrl}
        label="Webhook URL"
        disabled
        variant="standard"
      />
    );
  }

  renderChannelSelect = () => {
    const { channels } = this.state;
    const {
      classes,
      errors,
      accountConnector: {
        remoteMeta: { channels: slackChannels = [], users: slackUsers = [] },
        status,
      },
    } = this.props;

    const channelOptions = combineUsersAndChannels(slackUsers, slackChannels);

    return (
      <FormControl
        error={hasError(errors, 'channels')}
        fullWidth
        required
        className={classes.field}>
        <FormLabel classes={{ root: 'flex mb-2' }}>
          Slack Channel(s)
          <InputLabelTooltip title="Choose the channel or channels you would like to send alerts to." />
        </FormLabel>
        <NestedMultiselect
          menuListIcon={<SlackLogo />}
          disabled={status !== 'active'}
          error={hasError(errors, 'channels')}
          fullWidth
          getOptionLabel={(option) => {
            return option.isChannel
              ? `#${option.name}`
              : `${option.realName} (@${option.displayName ||
                  option.realName})`;
          }}
          getOptionValue={(option) => option.id}
          onChange={(ids) => {
            this.handleFieldChange('channels', ids);
          }}
          options={channelOptions}
          placeholder="Choose a channel"
          required
          title="Channels"
          value={channels}
        />
      </FormControl>
    );
  };

  render() {
    const {
      accountConnector,
      attributes,
      classes,
      errors,
      refetch,
      segmentId,
      segmentType,
      typename,
    } = this.props;

    const {
      channels,
      columns,
      webhookUrl,
      message,
      duplicateSuppressionTimeframe,
      triggerType,
    } = this.state;

    if (!this.connectedSlack() && !webhookUrl) {
      return (
        <Disconnected service="slack" onSuccess={refetch} Logo={SlackLogo} />
      );
    }

    return (
      <div>
        <h2 className={classes.subheading}>Trigger</h2>
        <span className={classes.triggerSubtext}>
          Choose when this Destination will be run.{' '}
          <a
            href="https://help.clearbit.com/hc/en-us/articles/360023453694"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline' }}>
            Learn more
          </a>
        </span>

        <TriggerType
          errors={errors}
          handleChange={this.handleFieldChange}
          triggerType={triggerType}
          triggerTypes={defaultTriggerTypes(typename)}
        />

        <h2 className={classes.subheading}>Configure Slack options</h2>

        <div className={classes.contentBox}>
          {webhookUrl && (
            <SlackConnectorStatus
              accountConnector={accountConnector}
              onSuccess={refetch}
              webhookUrl={webhookUrl}
            />
          )}

          {this.connectedSlack()
            ? this.renderChannelSelect()
            : this.renderWebhookInput()}

          <FormControl
            error={hasError(errors, 'channels')}
            fullWidth
            required
            className={classes.field}>
            <FormLabel classes={{ root: 'flex mb-2' }}>
              Custom Message
              <InputLabelTooltip title="Include a custom message at the top of your Slack post" />
            </FormLabel>
            <TextField
              error={hasError(errors, 'message')}
              fullWidth
              defaultValue={message}
              onChange={(event) => {
                this.handleFieldChange('message', event.target.value);
              }}
              variant="standard"
            />
          </FormControl>
          <hr className={classes.break} />
          <SuppressionForm
            typename={readableSingularSegmentType(typename)}
            exclusion_timeframe={duplicateSuppressionTimeframe}
            onChange={({ exclusion_timeframe }) => {
              this.handleFieldChange(
                'duplicateSuppressionTimeframe',
                exclusion_timeframe,
              );
            }}
          />
        </div>

        <h2 className={classes.subheading}>Customize your Slack message</h2>

        <p className={classes.columnsHelperText}>
          Select which attributes you want to include as fields in your Slack
          message
        </p>

        <MultiPicklist
          allColumns={attributes}
          onChange={(value) => {
            this.handleFieldChange('columns', value);
          }}
          type={legacySegmentType(typename)}
          value={columns || []}
        />

        <h2 className={classes.subheading}>Test this destination</h2>
        <span className={classes.triggerSubtext}>
          Run a test by manually triggering this destination
        </span>
        <div className={classes.contentBox}>
          <DeliverTestButton
            config={{
              columns,
              duplicateSuppressionTimeframe,
              message,
              channels,
              webhookUrl,
            }}
            destinationType="slack"
            label="Slack message"
            segmentId={segmentId}
            segmentType={segmentType}>
            <p>Trigger an example Slack alert</p>
          </DeliverTestButton>
        </div>
      </div>
    );
  }
}

Slack.propTypes = {
  accountConnector: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    remoteMeta: PropTypes.shape({
      channels: PropTypes.array.isRequired,
      users: PropTypes.array.isRequired,
    }).isRequired,
  }).isRequired,
  attributes: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  destination: PropTypes.object,
  errors: PropTypes.array.isRequired,
  onInputChange: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  segmentId: PropTypes.string.isRequired,
  segmentType: PropTypes.string.isRequired,
  typename: PropTypes.string.isRequired,
};

export default withStyles(
  (theme) => ({
    ...sharedStyles(theme),
    ...styles(theme),
  }),
  { name: 'SegmentDestinationNewSlack' },
)(Slack);
